import React, { useEffect, useMemo } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonText,
  IonButtons,
  IonBackButton,
} from '@ionic/react';
import { useAuth } from '@clerk/clerk-react';
import { useHistory, useParams } from 'react-router-dom';
import { useI18n } from '../i18n/I18nProvider';
import { usePageView } from '../hooks/useLoggerInit';
import {
  clearPendingReferralCode,
  savePendingReferralCode,
} from '../lib/localStore';

/**
 * Public deep-link entry: /r/:code
 * Stores the code for claim-after-signup. Existing signed-in users are not linked.
 */
const ReferralLandingPage: React.FC = () => {
  const { code: rawCode } = useParams<{ code: string }>();
  const code = useMemo(
    () => (rawCode || '').trim().toUpperCase(),
    [rawCode]
  );
  const { isSignedIn, isLoaded } = useAuth();
  const history = useHistory();
  const { t } = useI18n();
  usePageView('referral_landing');

  useEffect(() => {
    if (!code) {
      history.replace('/');
      return;
    }
    if (!isLoaded) return;

    if (isSignedIn) {
      clearPendingReferralCode();
      history.replace('/dashboard');
      return;
    }

    savePendingReferralCode(code);
  }, [code, isLoaded, isSignedIn, history]);

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/" text={t('common.actions.back')} />
          </IonButtons>
          <IonTitle>{t('referral.landing.title')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding vault-screen">
        <div className="referral-landing">
          <h1>{t('referral.landing.headline')}</h1>
          <p>{t('referral.landing.subhead')}</p>
          <IonText color="medium">
            <p className="referral-landing__note">
              {t('referral.landing.existingNote')}
            </p>
          </IonText>
          <div className="referral-landing__actions">
            <IonButton
              expand="block"
              disabled={!code}
              onClick={() => {
                if (code) savePendingReferralCode(code);
                history.push('/sign-up');
              }}
            >
              {t('referral.landing.signUp')}
            </IonButton>
            <IonButton
              expand="block"
              fill="outline"
              onClick={() => {
                clearPendingReferralCode();
                history.push('/sign-in');
              }}
            >
              {t('referral.landing.signIn')}
            </IonButton>
          </div>
        </div>
        <style>{`
          .referral-landing {
            max-width: 420px;
            margin: 2rem auto 0;
            text-align: center;
          }
          .referral-landing h1 {
            margin: 0 0 0.5rem;
            font-size: 1.5rem;
          }
          .referral-landing p {
            margin: 0 0 0.75rem;
            color: var(--ion-color-medium);
            line-height: 1.45;
          }
          .referral-landing__note {
            font-size: 0.85rem;
          }
          .referral-landing__actions {
            display: flex;
            flex-direction: column;
            gap: 0.65rem;
            margin-top: 1.5rem;
          }
        `}</style>
      </IonContent>
    </IonPage>
  );
};

export default ReferralLandingPage;
